import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiArrowRight, FiMenu, FiX } from 'react-icons/fi';
import BrandLogo from './BrandLogo';
import { useAuth } from '../context/AuthContext';

// Reusable NavLink component for desktop
const NavLink = ({ to, children, onClick, isActive = false }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 ${
      isActive
        ? 'bg-cyan-300/15 text-cyan-100 shadow-[inset_0_0_0_1px_rgba(103,232,249,0.28)]'
        : 'text-slate-300 hover:bg-white/8 hover:text-white'
    }`}
  >
    {children}
  </Link>
);

// Reusable MobileNavLink component for mobile
const MobileNavLink = ({ to, children, onClick, isActive = false }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`block rounded-2xl px-4 py-3 text-base font-medium transition-colors ${
      isActive
        ? 'bg-cyan-300/12 text-cyan-100'
        : 'text-slate-300 hover:bg-white/8 hover:text-white'
    }`}
  >
    {children}
  </Link>
);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthed, isLoading, logout, user, subscriptionStatus } = useAuth();

  // Helper to check if a link is active
  const isLinkActive = (to) => location.pathname === to;

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const nav = document.querySelector('nav');
      if (isOpen && nav && !nav.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close mobile menu when route changes
  const closeMobileMenu = () => {
    setIsOpen(false);
  };

  const handleLogout = async () => {
    closeMobileMenu();
    await logout();
    navigate('/login', { replace: true });
  };

  const isAuthenticated = !isLoading && isAuthed;
  const canOpenAccount = ['active', 'lifetime', 'trial', 'trialing'].includes(subscriptionStatus);
  const authPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];
  const isAuthPage = authPaths.includes(location.pathname);
  const displayName = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email?.split('@')[0] || 'Trader';
  const brandTarget = isAuthenticated && canOpenAccount ? '/account' : '/';

  const navLinks = [
    { to: '/', text: 'Home' },
    { to: '/about', text: 'About' },
    { to: '/pricing', text: 'Pricing' },
    { to: '/reviews', text: 'Reviews' },
    { to: '/contact', text: 'Contact' },
  ];

  return (
    <nav className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-6 lg:px-8">
      <div
        className={`mx-auto max-w-7xl rounded-[1.75rem] border transition-all duration-300 ${
          scrolled
            ? 'border-white/12 bg-[#09131dcc] shadow-[0_18px_60px_rgba(0,0,0,0.35)] backdrop-blur-2xl'
            : 'border-white/8 bg-[#08111aaa] backdrop-blur-xl'
        }`}
      >
        <div className="flex h-[74px] items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between w-full md:w-auto">
            <BrandLogo to={brandTarget} onClick={closeMobileMenu} size="compact" />
            
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-3 text-slate-300 transition hover:bg-white/10 hover:text-white focus:outline-none"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                {isOpen ? (
                  <FiX className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <FiMenu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-1 rounded-full border border-white/8 bg-white/4 p-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={closeMobileMenu}
                  isActive={isLinkActive(link.to)}
                >
                  {link.text}
                </NavLink>
              ))}
            </div>
            
            {isAuthenticated && !isAuthPage ? (
              <div className="ml-4 flex items-center gap-3">
                <div className="rounded-full border border-white/12 bg-white/5 px-5 py-3 text-sm font-semibold text-slate-200">
                  {displayName}
                </div>
                {canOpenAccount && (
                  <Link 
                    to="/account" 
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 px-5 py-3 text-sm font-bold text-slate-950 transition hover:scale-[1.02]"
                    onClick={closeMobileMenu}
                  >
                    User Account
                    <FiArrowRight className="h-4 w-4" />
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="rounded-full border border-white/12 bg-white/5 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/10 focus:outline-none"
                  aria-label="Sign out"
                  type="button"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="ml-4 flex items-center gap-2">
                <Link 
                  to="/login" 
                  className="rounded-full border border-white/12 bg-white/5 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/10"
                  onClick={closeMobileMenu}
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 px-5 py-3 text-sm font-bold text-slate-950 transition hover:scale-[1.02]"
                  onClick={closeMobileMenu}
                >
                  Start trial
                  <FiArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`mx-auto max-w-7xl overflow-hidden transition-all duration-300 ease-in-out md:hidden ${isOpen ? 'max-h-[32rem] pt-3' : 'max-h-0'}`}>
        <div className="rounded-[1.75rem] border border-white/10 bg-[#09131df2] p-3 shadow-[0_18px_60px_rgba(0,0,0,0.35)] backdrop-blur-2xl">
          <div className="space-y-1">
          {navLinks.map((link) => (
            <MobileNavLink
              key={link.to}
              to={link.to}
              onClick={closeMobileMenu}
              isActive={isLinkActive(link.to)}
            >
              {link.text}
            </MobileNavLink>
          ))}
          </div>
          
            {isAuthenticated && !isAuthPage ? (
            <>
              <div className="mt-3 border-t border-white/10 pt-4 pb-2">
                <div className="px-3 py-2 text-sm text-slate-400">
                  Signed in as {displayName}
                </div>
                {canOpenAccount && (
                  <MobileNavLink to="/account" onClick={closeMobileMenu}>
                    User Account
                  </MobileNavLink>
                )}
              </div>
              <div className="border-t border-white/10 pt-3 pb-2">
                <button
                  onClick={() => {
                    handleLogout();
                  }}
                  className="mt-2 block w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-left text-base font-bold text-white transition hover:bg-white/10 focus:outline-none"
                  aria-label="Sign out"
                  type="button"
                >
                  Logout
                </button>
              </div>
            </>
          ) : (
            <div className="mt-3 border-t border-white/10 pt-4 pb-2">
              <div className="flex flex-col space-y-2 px-1">
                <Link
                  to="/login"
                  className="flex w-full items-center justify-center rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base font-bold text-white transition hover:bg-white/10"
                  onClick={closeMobileMenu}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 px-4 py-3 text-base font-bold text-slate-950 transition hover:scale-[1.01]"
                  onClick={closeMobileMenu}
                >
                  Start free trial
                  <FiArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
